import type { RouteObject } from 'react-router-dom';
import { lazyRetry } from '../lib/lazyRetry';
import LangLayout from './LangLayout';

// lazyRetry === React.lazy + stale-deploy resilience (T67): a chunk 404 from an
// old deploy self-heals with one guarded reload instead of the error screen.
// The second arg is a stable per-chunk id keying that reload guard.
const Home = lazyRetry(() => import('../pages/home/page'), 'home');
const About = lazyRetry(() => import('../pages/about/page'), 'about');
const Services = lazyRetry(() => import('../pages/services/page'), 'services');
const ServiceDetail = lazyRetry(() => import('../pages/services/detail'), 'service-detail');
const GuidePage = lazyRetry(() => import('../pages/guide/page'), 'guide');
// T243 — the guides INDEX. `/guides/:slug` has existed since the guides shipped;
// `/guides` itself never did, so the three reading pages had no list, the nav's
// Resources menu had no Guides row (it would have pointed at a 404 — Header.tsx's
// T234 note), and every guide's "All guides" exit had nowhere to go. It goes in
// pageChildren() like any other public page: both language URLs, the language
// switcher, and a place in the crawl inventory e2e derives from this factory.
const GuidesHub = lazyRetry(() => import('../pages/guides/page'), 'guides');
const Contact = lazyRetry(() => import('../pages/contact/page'), 'contact');
const Pricing = lazyRetry(() => import('../pages/pricing/page'), 'pricing');
const Testimonials = lazyRetry(() => import('../pages/testimonials/page'), 'testimonials');
const FAQ = lazyRetry(() => import('../pages/faq/page'), 'faq');
const Methodology = lazyRetry(() => import('../pages/methodology/page'), 'methodology');
const PolicyUpdates = lazyRetry(() => import('../pages/policy-updates/page'), 'policy-updates');
const PathwayFinder = lazyRetry(() => import('../pages/pathway-finder/page'), 'pathway-finder');
// T336 — the live CRS calculator. Public + localized (pageChildren ⇒ /crs-calculator and /zh/crs-calculator).
const CrsCalculator = lazyRetry(() => import('../pages/crs-calculator/page'), 'crs-calculator');
const BookConsultation = lazyRetry(() => import('../pages/book-consultation/page'), 'book-consultation');
// T269 — the specimen-document route and its verification twin were REMOVED
// (owner ruling 2026-07-31). The report pipeline itself is untouched: it lives
// in the admin review desk, which is where a document is drafted, reviewed and
// signed off. What the public face carries now is the questionnaire loop
// (/consult-prep), not a published specimen of a document.
const BookingSuccess = lazyRetry(() => import('../pages/booking-success/page'), 'booking-success');
// T237 — the legal pages. PUBLIC and localized like the rest of pageChildren():
// the footer links them from every page, a privacy policy that only exists in
// English is not a privacy policy for half our readers, and both are pages a
// regulator or a reader may cite by URL.
const Privacy = lazyRetry(() => import('../pages/privacy/page'), 'privacy');
const Terms = lazyRetry(() => import('../pages/terms/page'), 'terms');
const NotFound = lazyRetry(() => import('../pages/NotFound'), 'not-found');
const Admin = lazyRetry(() => import('../pages/admin/page'), 'admin');

// F1a client-intake surface — its own routes (noindex; NOT under LangLayout, like
// /admin). Purely additive; existing pages untouched (INV-2).
const IntakeInvite = lazyRetry(() => import('../pages/intake/InvitePage'), 'intake-invite');
const IntakeLogin = lazyRetry(() => import('../pages/intake/LoginPage'), 'intake-login');
const IntakeDashboard = lazyRetry(() => import('../pages/intake/DashboardPage'), 'intake-dashboard');
const IntakeCase = lazyRetry(() => import('../pages/intake/CasePage'), 'intake-case');
const IntakePreview = lazyRetry(() => import('../pages/intake/PreviewPage'), 'intake-preview');
// [T153] dev-only workbench harness (the /intake/preview precedent) — the
// mobile e2e + owner walkthrough surface for the case-detail three-zone layout.
const AdminCasePreview = lazyRetry(() => import('../pages/admin/PreviewCasePage'), 'admin-case-preview');

// T21 Stage-1 triage — public free-preview surface (noindex until launch; NOT
// under LangLayout, like /intake). Purely additive; existing routes untouched.
const TriageIntake = lazyRetry(() => import('../pages/triage/TriagePage'), 'triage-intake');
const TriageResult = lazyRetry(() => import('../pages/triage/TriagePreview'), 'triage-result');

// T168 pre-consultation light questionnaire — paid-client surface reached from
// the booking-success CTA / confirmation email (noindex; NOT under LangLayout,
// like /triage — it carries its own bilingual toggle). Purely additive.
const ConsultPrep = lazyRetry(() => import('../pages/consult-prep/page'), 'consult-prep');

// T278 client reschedule ask — paid-client surface reached from the
// confirmation email's reschedule link (the T279 leg). Registered EXACTLY as
// /consult-prep is (noindex; NOT under LangLayout — own bilingual toggle;
// absent from sitemap/prerender/public-route inventories). Purely additive.
// ⚠ Off-face twins reported on docs/tasks/T278.md for the hub: the
// NON_LOCALIZED_PREFIXES entry (src/lib/lang.ts) and the App.tsx portal-class
// line — both one-liners /consult-prep already carries.
const Reschedule = lazyRetry(() => import('../pages/reschedule/page'), 'reschedule');

// T220 Track U·U1 — the v2 component reference展台. Internal surface: noindex
// (runtime meta), absent from postbuild's prerender/sitemap allowlists, linked
// from nowhere.
const DesignReference = lazyRetry(() => import('../pages/design-reference/page'), 'design-reference');

// Shared page tree, mounted under both the English root ('/') and the Chinese
// prefix ('/zh'). A fresh array per language avoids any shared-reference surprises.
const pageChildren = (): RouteObject[] => [
  { index: true, element: <Home /> },
  { path: 'about', element: <About /> },
  { path: 'services', element: <Services /> },
  { path: 'services/:slug', element: <ServiceDetail /> },
  { path: 'guides', element: <GuidesHub /> },
  { path: 'guides/:slug', element: <GuidePage /> },
  { path: 'pricing', element: <Pricing /> },
  { path: 'testimonials', element: <Testimonials /> },
  { path: 'faq', element: <FAQ /> },
  { path: 'methodology', element: <Methodology /> },
  { path: 'policy-updates', element: <PolicyUpdates /> },
  { path: 'pathway-finder', element: <PathwayFinder /> },
  { path: 'crs-calculator', element: <CrsCalculator /> },
  { path: 'contact', element: <Contact /> },
  { path: 'book-consultation', element: <BookConsultation /> },
  { path: 'booking-success', element: <BookingSuccess /> },
  { path: 'privacy', element: <Privacy /> },
  { path: 'terms', element: <Terms /> },
];

// T220: the reference展台 mounts INSIDE LangLayout (so it inherits the real site
// chrome it will be assembled into) but deliberately NOT via pageChildren().
// pageChildren() is parsed by e2e/tests/helpers/publicRoutes.mjs to derive the
// public crawl inventory, which drives the click-crawl, the all-routes viewport
// guard and the zoom guard. Adding an internal fixture page there would enlist it
// in three e2e suites and move their baselines — and this card must not touch e2e
// (write-face). Mounted in BOTH languages so the header's language switcher does
// not build a dead /zh link on it (the T75 bug shape); the page itself is English.
const designReferenceChild = (): RouteObject => ({ path: 'design-reference', element: <DesignReference /> });

const routes: RouteObject[] = [
  { path: '/', element: <LangLayout lang="en" />, children: [...pageChildren(), designReferenceChild()] },
  { path: '/zh', element: <LangLayout lang="zh" />, children: [...pageChildren(), designReferenceChild()] },
  { path: '/admin', element: <Admin /> },
  { path: '/admin/appointments', element: <Admin /> },
  { path: '/intake/invite', element: <IntakeInvite /> },
  { path: '/intake/login', element: <IntakeLogin /> },
  { path: '/intake', element: <IntakeDashboard /> },
  { path: '/intake/case/:id', element: <IntakeCase /> },
  // Dev-only design harness (excluded from production builds — INV-1).
  ...(import.meta.env.DEV ? [{ path: '/intake/preview', element: <IntakePreview /> }] : []),
  ...(import.meta.env.DEV ? [{ path: '/admin/preview-case', element: <AdminCasePreview /> }] : []),
  // T21 triage free-preview (noindex; public, no auth).
  { path: '/triage', element: <TriageIntake /> },
  { path: '/triage/preview', element: <TriageResult /> },
  // T168 pre-consultation questionnaire (noindex; token-gated via consult-intake).
  { path: '/consult-prep', element: <ConsultPrep /> },
  // T278 client reschedule ask (noindex; pair-gated via reschedule-request).
  { path: '/reschedule', element: <Reschedule /> },
  { path: '*', element: <NotFound /> },
];

export default routes;
